import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login({ email, password });
      toast.success("Bentornato!");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1768321611594-c944cf4bc0be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxzZWNvbmQlMjBoYW5kJTIwZnVybml0dXJlJTIwaG9tZXxlbnwwfHx8fDE3ODQ5OTIyMjd8MA&ixlib=rb-4.1.0&q=85" alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute bottom-10 left-10 text-white max-w-sm">
          <h2 className="font-head font-extrabold text-3xl">Regalare fa bene.<br />A te e al pianeta.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Gift className="h-5 w-5 text-primary-foreground" /></div>
            <span className="font-head font-extrabold text-xl text-primary">Ti Regalo</span>
          </Link>
          <h1 className="font-head font-extrabold text-3xl">Accedi</h1>
          <p className="text-muted-foreground mt-2">Bentornato nella community.</p>

          <form onSubmit={submit} className="space-y-4 mt-8">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="mario@example.it" className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" className="h-11 rounded-xl" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 rounded-full">{loading ? "Accesso..." : "Accedi"}</Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground uppercase tracking-widest">oppure</span><div className="h-px flex-1 bg-border" />
          </div>

          <Button variant="outline" onClick={googleLogin} data-testid="google-login" className="w-full h-11 rounded-full">
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" /> Continua con Google
          </Button>

          <p className="text-sm text-center text-muted-foreground mt-8">
            Non hai un account? <Link to="/register" className="text-primary font-semibold" data-testid="to-register">Iscriviti</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
